/**
 * Resolves a bilingual (EN/ES) field pair against a Staff member's
 * `language_preference` for display. Used on the content-facing screens
 * (FlowListPage, FlowPlayerPage) — never hides a translation behind a
 * hover/tooltip; "BOTH" always renders both directly, since the content
 * here (spiels, branch choices) can be read live mid-call.
 */
import type { LanguagePreference } from "@/types/api";

export interface BilingualPair {
  en: string;
  es: string;
}

export interface ResolvedText {
  /** Always has a value — English if nothing else is available. */
  primary: string;
  /** Set only when preference is BOTH and the Spanish text differs from English. */
  secondary: string | null;
  /** True when ES was requested but blank, so English was substituted. */
  isFallback: boolean;
}

export function resolveBilingual(pref: LanguagePreference, pair: BilingualPair): ResolvedText {
  const en = pair.en.trim();
  const es = pair.es.trim();

  if (pref === "EN") {
    return { primary: pair.en, secondary: null, isFallback: false };
  }

  if (pref === "ES") {
    if (es) return { primary: pair.es, secondary: null, isFallback: false };
    return { primary: pair.en, secondary: null, isFallback: true };
  }

  // BOTH — show English + Spanish unless Spanish is blank or identical.
  if (es && es !== en) {
    return { primary: pair.en, secondary: pair.es, isFallback: false };
  }
  return { primary: pair.en, secondary: null, isFallback: false };
}
